/**
 * VH-107: AI Recipe Scaling dialog.
 *
 * State machine:
 *   input              — user types a free-text target ("1.5 kg loaf", "28 cm ring")
 *   loading            — POST /recipes/{id}/scale-suggest in flight
 *   editable_form      — AI returned a scaled draft; the user reviews + edits the
 *                        prefilled CaptureManualForm and the reasoning panel.
 *                        Confirming saves it as a new version (POST /recipes/{id}/fork).
 *                        (VH-249 removed the "use for shopping list only" path
 *                        with the shopping-list feature.)
 *
 * Once "Save as new version" succeeds the dialog closes and RecipeDetail
 * refetches via useForkVersion.
 */

import { useEffect, useMemo, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Dialog } from '@headlessui/react'
import {
  XMarkIcon,
  SparklesIcon,
  ArrowPathIcon,
  ArrowLeftIcon,
} from '@heroicons/react/24/outline'
import toast from 'react-hot-toast'
import CaptureManualForm from './CaptureManualForm'
import {
  useScaleRecipeSuggest,
  useForkVersion,
  useRecipeCategories,
} from '../../hooks/useRecipes'
import { contentLanguage } from '../../i18n/supported'

// Map backend status codes to user-facing error keys.
function mapErrorKey(err) {
  const status = err?.response?.status
  if (status === 429) return 'recipes.scale.errorQuota'
  if (status === 422) {
    const detail = err?.response?.data?.detail || ''
    // Backend may return either "out of range" or "cannot parse" — both via 422.
    // The detail string is per-language already; we map heuristically here for
    // a friendlier UI message. If the heuristic misses, fall back to generic.
    if (/range|too small|too large|klein|groß|grande|pequeno/i.test(detail))
      return 'recipes.scale.errorOutOfRange'
    return 'recipes.scale.errorUnparseable'
  }
  if (status === 502) return 'recipes.scale.errorAI'
  return 'recipes.scale.errorGeneric'
}

// Derive an auto-generated branch label from the target text. Trimmed to
// ~40 chars so it fits the existing branch_label VARCHAR(60).
function branchLabelFromTarget(target) {
  const cleaned = (target || '').trim().replace(/\s+/g, ' ')
  if (!cleaned) return 'Scaled variant'
  const truncated = cleaned.length > 40 ? cleaned.slice(0, 40) + '…' : cleaned
  return `Scaled: ${truncated}`
}

// Build a CaptureManualForm-compatible initialValue from the source version
// + the AI scaling suggestion. AI fields override; everything not in the AI
// response is carried over from the parent. category_id is required by the
// CaptureManualForm validation so we always pass it through unchanged.
function buildScaledInitialValue({ recipe, version, suggestion }) {
  if (!version || !suggestion) return null
  // The AI returns scaled ingredients in the same shape the form expects.
  // We preserve `id` from the source row where present so the user sees
  // the ingredient list stably ordered; the fork endpoint ignores `id`
  // and creates fresh rows on the new version.
  const scaledIngredients = (suggestion.ingredients || []).map((ing, idx) => ({
    food_id: ing.food_id ?? null,
    food_name_snapshot: ing.food_name_snapshot || ing.name || '',
    category_snapshot: ing.category_snapshot || 'other',
    amount: ing.amount,
    unit: ing.unit || 'g',
    notes: ing.notes || '',
    display_order: idx,
  }))
  const scaledSteps = (suggestion.steps || []).map((s, idx) => ({
    step_order: s.step_order ?? idx + 1,
    text: s.text || '',
    timer_minutes: s.timer_minutes ?? null,
    min_oven_temp_c: s.min_oven_temp_c ?? null,
  }))
  return {
    title: version.title,
    description: version.description || '',
    tips: version.tips || '',
    photo_url: '', // scaled version inherits via parent-chain walk
    servings: suggestion.servings ?? version.servings,
    prep_minutes: suggestion.prep_minutes ?? version.prep_minutes,
    cook_minutes: suggestion.cook_minutes ?? version.cook_minutes,
    ingredients: scaledIngredients,
    steps: scaledSteps,
    equipment_keys: version.equipment_keys || [],
    category_id: recipe.category_id,
  }
}

export default function ScaleRecipeDialog({
  open,
  onClose,
  recipe,
  version,
}) {
  const { t, i18n } = useTranslation()
  const lang = contentLanguage(i18n?.language)
  const recipeId = recipe?.id

  const [phase, setPhase] = useState('input') // input | loading | editable_form | pick_list
  const [target, setTarget] = useState('')
  const [errorKey, setErrorKey] = useState(null)
  const [suggestion, setSuggestion] = useState(null)

  // Reset whenever the dialog is reopened.
  useEffect(() => {
    if (open) {
      setPhase('input')
      setTarget('')
      setErrorKey(null)
      setSuggestion(null)
    }
  }, [open])

  const scaleMutation = useScaleRecipeSuggest(recipeId)
  const forkMutation = useForkVersion(recipeId)
  const { data: categories = [] } = useRecipeCategories(lang)

  const initialFormValue = useMemo(
    () => buildScaledInitialValue({ recipe, version, suggestion }),
    [recipe, version, suggestion]
  )

  // --- input phase ---------------------------------------------------

  const handleSubmitTarget = async (e) => {
    e?.preventDefault?.()
    const cleaned = (target || '').trim()
    if (!cleaned) {
      setErrorKey('recipes.scale.errorEmptyTarget')
      return
    }
    setErrorKey(null)
    setPhase('loading')
    try {
      const result = await scaleMutation.mutateAsync(cleaned)
      setSuggestion(result)
      setPhase('editable_form')
    } catch (err) {
       
      console.error('[ScaleRecipeDialog] scale failed', err)
      setErrorKey(mapErrorKey(err))
      setPhase('input')
    }
  }

  // --- save-as-new-version path -------------------------------------

  const handleSaveAsVersion = async (payload) => {
    if (!version) return
    try {
      await forkMutation.mutateAsync({
        from_version_id: version.id,
        branch_label: branchLabelFromTarget(target),
        version: {
          ...payload.version,
          change_reason: suggestion?.reasoning || null,
          branch_label: branchLabelFromTarget(target),
        },
      })
      toast.success(t('recipes.scale.savedToast'))
      onClose?.()
    } catch (err) {
       
      console.error('[ScaleRecipeDialog] fork failed', err)
      toast.error(t('recipes.scale.saveFailed'))
    }
  }

  // --- form submit ------------------------------------------------

  // Capture the latest edited payload from CaptureManualForm. The form
  // doesn't expose an onChange — its onSubmit is the only hook. So we
  // give the action button its own form submit
  // by sharing a single hidden submit channel: we render CaptureManualForm
  // with onSubmit that dispatches based on which action button the user
  // last clicked.
  //
  // This MUST be a ref, not useState: the action buttons call
  // setPendingAction(...) and then synchronously trigger
  // form.requestSubmit(). CaptureManualForm calls onSubmit synchronously
  // (plain controlled state, not react-hook-form), so handleFormSubmit runs
  // before React flushes a state update — a useState value would still read
  // its stale previous value (null), and neither branch below would fire,
  // making "Save as new version" silently do nothing.
  const pendingActionRef = useRef(null) // 'save'
  const setPendingAction = (action) => {
    pendingActionRef.current = action
  }

  const handleFormSubmit = (payload) => {
    const action = pendingActionRef.current
    pendingActionRef.current = null
    if (action === 'save') {
      handleSaveAsVersion(payload)
    }
  }

  // --- close handler -------------------------------------------------

  const busy =
    scaleMutation.isPending || forkMutation.isPending || phase === 'loading'

  const handleClose = () => {
    if (busy) return
    onClose?.()
  }

  // Backdrop / Escape are intentionally ignored — users were losing AI-scaled
  // drafts by accidentally clicking outside the panel. Closing requires the
  // explicit X / Cancel button so the (possibly inline-edited) draft is not
  // discarded by a stray click.
  const handleDialogClose = () => {}

  // --- render --------------------------------------------------------

  return (
    <Dialog
      open={open}
      onClose={handleDialogClose}
      className="relative z-50"
    >
      <div className="fixed inset-0 bg-black/30" aria-hidden="true" />
      {/* VH-139: the scroll lives on the PANEL, not on this wrapper.
              Headless UI treats a mousedown anywhere outside `Dialog.Panel` as
              a request to close — and when the wrapper is what scrolls, its
              scrollbar IS outside the panel. Dragging it closed the dialog, so
              editing a long recipe "switched to view mode" the moment you
              reached for the scrollbar. Wheel scrolling never showed it, which
              is why it survived: the pointer stays inside the panel.

              Keeping the wrapper unscrollable means the only scrollbar belongs
              to the panel, and dragging it is an inside event. */}
        <div className="fixed inset-0 flex items-start justify-center p-4 overflow-hidden">
        <Dialog.Panel
          className="w-full max-w-3xl bg-surface-container rounded-lg shadow-xl mt-8 mb-8 max-h-[90vh] overflow-y-auto"
          data-testid="scale-recipe-dialog"
        >
          <div className="flex items-center justify-between px-5 py-3 border-b border-gray-200">
            <Dialog.Title className="text-lg font-semibold flex items-center gap-2">
              <SparklesIcon className="h-5 w-5 text-primary-600" />
              {t('recipes.scale.dialogTitle')}
            </Dialog.Title>
            <button
              type="button"
              onClick={handleClose}
              disabled={busy}
              className="p-1 text-gray-400 hover:text-gray-600 rounded-full hover:bg-gray-100 disabled:opacity-50"
              aria-label={t('recipes.scale.cancel')}
            >
              <XMarkIcon className="h-5 w-5" />
            </button>
          </div>

          <div className="p-5">
            {(phase === 'input' || phase === 'loading') && (
              <InputPhase
                t={t}
                target={target}
                setTarget={setTarget}
                onSubmit={handleSubmitTarget}
                loading={phase === 'loading'}
                errorKey={errorKey}
              />
            )}

            {phase === 'editable_form' && suggestion && initialFormValue && (
              <EditablePhase
                t={t}
                target={target}
                suggestion={suggestion}
                initialValue={initialFormValue}
                categories={categories}
                forking={forkMutation.isPending}
                onCancel={() => setPhase('input')}
                onSetAction={setPendingAction}
                onFormSubmit={handleFormSubmit}
              />
            )}

          </div>
        </Dialog.Panel>
      </div>
    </Dialog>
  )
}

// =====================================================================
// Phase: input
// =====================================================================

function InputPhase({ t, target, setTarget, onSubmit, loading, errorKey }) {
  return (
    <form onSubmit={onSubmit} className="space-y-4">
      <div>
        <label className="form-label" htmlFor="scale-target-input">
          {t('recipes.scale.targetField')}
        </label>
        <input
          id="scale-target-input"
          type="text"
          value={target}
          onChange={(e) => setTarget(e.target.value)}
          placeholder={t('recipes.scale.targetPlaceholder')}
          maxLength={200}
          autoFocus
          disabled={loading}
          className="form-input w-full"
          data-testid="scale-target-input"
        />
        <p className="text-xs text-gray-500 mt-1">
          {t('recipes.scale.targetHelp')}
        </p>
      </div>

      {errorKey && (
        <div
          className="rounded-md border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-800"
          data-testid="scale-error"
          role="alert"
        >
          <div className="font-medium">{t('recipes.scale.errorTitle')}</div>
          <div>{t(errorKey)}</div>
        </div>
      )}

      {loading && (
        <div
          className="rounded-md border border-blue-200 bg-blue-50 px-3 py-2 text-sm text-blue-800 flex items-center gap-2"
          data-testid="scale-loading"
          aria-live="polite"
        >
          <ArrowPathIcon className="h-4 w-4 animate-spin" />
          <span>{t('recipes.scale.loadingHint')}</span>
        </div>
      )}

      <div className="flex justify-end">
        <button
          type="submit"
          disabled={loading}
          className="btn-primary flex items-center gap-2"
          data-testid="scale-submit"
        >
          {loading ? (
            <>
              <ArrowPathIcon className="h-4 w-4 animate-spin" />
              {t('recipes.scale.submitting')}
            </>
          ) : (
            <>
              <SparklesIcon className="h-4 w-4" />
              {t('recipes.scale.submit')}
            </>
          )}
        </button>
      </div>
    </form>
  )
}

// =====================================================================
// Phase: editable_form
// =====================================================================

function EditablePhase({
  t,
  target,
  suggestion,
  initialValue,
  categories,
  forking,
  onCancel,
  onSetAction,
  onFormSubmit,
}) {
  const branchLabel = branchLabelFromTarget(target)

  return (
    <div className="space-y-4">
      <div>
        <h3 className="text-base font-semibold text-gray-900">
          {t('recipes.scale.scaledTitle')}
        </h3>
        <p className="text-sm text-gray-600">
          {t('recipes.scale.scaledSubtitle')}
        </p>
      </div>

      {suggestion.reasoning && (
        <div
          className="rounded-md border border-amber-200 bg-amber-50 px-3 py-2 text-sm text-amber-900"
          data-testid="scale-reasoning"
        >
          <div className="font-medium flex items-center gap-1">
            <SparklesIcon className="h-4 w-4" />
            {t('recipes.scale.reasoningTitle')}
          </div>
          <div className="mt-0.5 whitespace-pre-line">
            {suggestion.reasoning}
          </div>
        </div>
      )}

      <div className="text-xs text-gray-500">
        {t('recipes.scale.branchLabelPreview', { label: branchLabel })}
      </div>

      {/* The CaptureManualForm submit button is hidden via CSS; we drive the
          form via the action button below which set the pending action
          and then trigger requestSubmit() on the form. */}
      <div className="scale-recipe-form-host">
        <CaptureManualForm
          initialValue={initialValue}
          categories={categories}
          onSubmit={onFormSubmit}
          submitting={forking}
        />
      </div>

      <style>{`
        /* Hide the inner submit button — actions are driven from outside. */
        .scale-recipe-form-host button[data-testid="submit-recipe"] {
          display: none;
        }
      `}</style>

      <div className="flex flex-wrap justify-end gap-2 pt-2 border-t border-gray-100">
        <button
          type="button"
          onClick={onCancel}
          disabled={forking}
          className="btn-secondary flex items-center gap-1"
          data-testid="scale-back-to-input"
        >
          <ArrowLeftIcon className="h-4 w-4" />
          {t('recipes.scale.back')}
        </button>
        <button
          type="button"
          disabled={forking}
          onClick={() => {
            onSetAction('save')
            const formEl = document.querySelector(
              '.scale-recipe-form-host form'
            )
            formEl?.requestSubmit?.()
          }}
          className="btn-primary flex items-center gap-1"
          data-testid="scale-save-as-version"
        >
          <SparklesIcon className="h-4 w-4" />
          {forking
            ? t('recipes.scale.saving')
            : t('recipes.scale.saveAsVersion')}
        </button>
      </div>
    </div>
  )
}

